/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getRouteInitData, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SelectionModel, emptySelectionModel, selectionModelAfterRowRemoved } from '@gh/core-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, map$, switchMap$, take$, takeUntil$ } from '@gh/rx/operators';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import {
  getHpeCostCenterId,
  getHpeItemId,
  HpeItem,
  HpeItemType,
  isHpeFreeItem,
  isHpeItemFromComponent,
} from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import {
  ensureHpeSalesTaxDivider,
  HpeDetailsState,
  HpeFocusedTable,
  queryHpeCostCenterItems,
  queryHpeEditable,
  queryHpeFocusedCell,
  queryHpeItem,
  queryHpeSelectedCostCenter,
  queryHpeSelectedCostCenterId,
  queryHpeSelectedItemId,
} from '../hpe-details.state';
import { HpeDetailsCcItemGridDefinition } from './hpe-details-cc-item-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-hpe-details-cc-item-list',
  templateUrl: 'hpe-details-cc-item-list.component.html',
  styleUrls: ['hpe-details-cc-item-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HpeDetailsCcItemGridDefinition],
})
export class HpeDetailsCcItemListComponent implements OnInit, OnDestroy {
  total$: Observable<number>;
  cost$: Observable<number>;
  combineCosts$: Observable<boolean>;
  items$: Observable<HpeItem[]>;
  selectedItemId$: Observable<Maybe<ID>>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  editable: boolean;

  private costCenterId: ID;
  private lastSeenCostCenterId: Maybe<ID>;
  private costCenterIdSubscription: Subscription;
  private readonly destroy$ = new Subject<void>();
  private selectionModelVersion$ = new BehaviorSubject<void>(undefined);

  sourceSelectionModel = emptySelectionModel();

  constructor(private route: ActivatedRoute,
              public gridDefinition: HpeDetailsCcItemGridDefinition,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService,
              private dialogService: DialogService) {

  }

  ngOnInit(): void {
    const { hpeItemGridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(hpeItemGridLayout);

    const { state } = this;
    const costCenterId$ = state.select(queryHpeSelectedCostCenterId).pipe(filter$(Boolean));
    const costCenter$ = state.select(queryHpeSelectedCostCenter).pipe(filter$(Boolean));

    this.items$ = costCenter$.pipe(
      switchMap$((cc: any) => state.select(queryHpeCostCenterItems, getHpeCostCenterId(cc)).pipe(
        map$(ensureHpeSalesTaxDivider))));
    this.total$ = costCenter$.pipe(map$((component: any) => component.total || 0));
    this.cost$ = costCenter$.pipe(map$((component: any) => component.cost || 0));
    this.combineCosts$ = combineLatest(this.cost$, this.total$).pipe(
      map$(([cost, total]) => cost === total));
    this.selectedItemId$ = state.select(queryHpeSelectedItemId);
    this.focusedCell$ = this.state.select(queryHpeFocusedCell, HpeFocusedTable.ItemList);

    this.costCenterIdSubscription = costCenterId$.subscribe((id: any) => {
      this.costCenterId = id;
      if (this.lastSeenCostCenterId !== void 0 && this.lastSeenCostCenterId !== id) {
        this.clearMultiSelectForCostCenterContextChange();
      }
      this.lastSeenCostCenterId = id;
    });
    this.editable = this.state.project(queryHpeEditable);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.costCenterIdSubscription.unsubscribe();
  }

  onAdd(): void {
    this.commands.costCenterItemAdd(this.costCenterId);
  }

  onhpeCloneItems(item: HpeItem): void {
    this.commands.costCenterItemClone(item);
  }

  onDelete(item: HpeItem): void {
    const id = getHpeItemId(item);
    this.commands.costCenterItemRemove(item).pipe(
      takeUntil$(this.destroy$),
    ).subscribe(() => {
      const items = this.state.project(queryHpeCostCenterItems, this.costCenterId) || [];
      const next = selectionModelAfterRowRemoved(
        this.sourceSelectionModel,
        id,
        items.length === 0,
      );
      if (next !== this.sourceSelectionModel) {
        this.onItemSelectionChanged(next);
      }
    });
  }

  canBeDeleted(item: HpeItem): boolean {
    return item.type === HpeItemType.Item;
  }

  onSelectedItemChanged(id?: ID): void {
    if (!id) {
      this.commands.itemSelect(void 0);
      return;
    }
    const item = this.state.project(queryHpeItem, id);
    if (item && (item.type === HpeItemType.Item || item.type === HpeItemType.Component)) {
      this.commands.itemSelect(id);
    }
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  filterCostCenterItem(item: HpeItem): boolean {
    return isHpeFreeItem(item) || isHpeItemFromComponent(item);
  }

  onItemSelectionChanged(model: SelectionModel): void {
    this.sourceSelectionModel = model;
    this.selectionModelVersion$.next(undefined);
  }

  private clearMultiSelectForCostCenterContextChange(): void {
    this.onItemSelectionChanged(emptySelectionModel());
  }

  getSelectedItems(): Observable<HpeItem[]> {
    return combineLatest([this.items$, this.selectionModelVersion$]).pipe(
      map$(([items]) => {
        if (!items || items.length === 0) {
          return [];
        }

        const { isExcludeMode, idList } = this.sourceSelectionModel;
        if (isExcludeMode) {
          return items.filter((item) =>
            !idList.includes(getHpeItemId(item)) && this.canBeDeleted(item));
        }
        return items.filter((item) =>
          idList.includes(getHpeItemId(item)) && this.canBeDeleted(item));
      }));
  }

  hasSelectedItems(): Observable<boolean> {
    return this.getSelectedItems().pipe(
      map$((selectedItems) => selectedItems.length > 0));
  }

  onDeleteSelected(): void {
    this.getSelectedItems().pipe(
      take$(1),
      filter$((selectedItems) => selectedItems.length > 0),
      switchMap$((selectedItems) => {
        const count = selectedItems.length;
        const confirmConfig = count === 1
          ? yesNoConfirmation('msg_bulk_remove_single_item', { code: selectedItems[0].code })
          : yesNoConfirmation('msg_bulk_remove_n_items', { count: String(count) });
        return this.dialogService.confirmation(confirmConfig).pipe(
          filter$(yesAnswer),
          map$(() => selectedItems));
      }),
    ).subscribe((selectedItems) => {
      this.commands.removeItems(selectedItems.map((item) => getHpeItemId(item)));
      this.sourceSelectionModel = emptySelectionModel();
      this.onItemSelectionChanged(this.sourceSelectionModel);
    });
  }
}
